import { useState } from "react";
import { toast } from "sonner";
import { resolveIdentifier } from "../../../lib/identifiers";
import { useUserProfiles } from "../../../hooks/useUserProfiles";
import { useUserSettings } from "../../../hooks/useUserSettings";
import { FriendCombobox } from "../../../components/FriendCombobox";
import { normalizeEmail } from "../../../lib/utils";
import {
  SimpleModal,
  SimpleModalDescription,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalTitle,
} from "../../../components/ui/simple-modal";

export function InviteMemberModal({ open, onOpenChange, group, onInviteMember, friends = [] }) {
  const { recentInvitees } = useUserSettings();
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [sendFriendInvite, setSendFriendInvite] = useState(false);

  const existingMembers = new Set(
    [
      ...(group?.members || []),
      ...(group?.pendingInvites || []),
    ]
      .map((email) => normalizeEmail(email))
      .filter(Boolean)
  );

  const friendSet = new Set(friends.map((email) => normalizeEmail(email)).filter(Boolean));
  const availableSuggestions = friends.filter(
    (email) => !existingMembers.has(normalizeEmail(email))
  );
  const { enrichUsers } = useUserProfiles(availableSuggestions);
  const suggestionUsers = enrichUsers(availableSuggestions);
  const normalizedEmail = normalizeEmail(email);
  const showFriendInviteToggle = Boolean(normalizedEmail);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!normalizedEmail) {
      setError("Please enter an email or Discord username");
      return;
    }

    let resolved;
    try {
      resolved = await resolveIdentifier(normalizedEmail);
    } catch (err) {
      setError(err.message || "Please enter a valid email or Discord username");
      return;
    }

    const resolvedEmail = normalizeEmail(resolved.email);
    if (existingMembers.has(resolvedEmail)) {
      setError("This person is already a member or has a pending invite");
      return;
    }

    setSaving(true);
    try {
      await onInviteMember(group.id, group.name, normalizedEmail, {
        sendFriendInvite,
      });
      setEmail("");
      setSendFriendInvite(false);
      onOpenChange(false);
      toast.success(`Invitation sent to ${resolvedEmail}`);
    } catch (err) {
      console.error("Failed to invite member:", err);
      setError(err.message || "Failed to send invitation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SimpleModal open={open} onOpenChange={onOpenChange}>
      <div className="max-w-md">
        <SimpleModalHeader>
          <SimpleModalTitle>Invite to {group?.name}</SimpleModalTitle>
          <SimpleModalDescription>
            Invite someone by email, Discord username, or @username. They'll receive an email and in-app notification.
          </SimpleModalDescription>
        </SimpleModalHeader>

        <form onSubmit={handleSubmit}>
          <div className="mt-4 space-y-4">
            <div>
              <label
                htmlFor="invite-member-combobox"
                className="block text-xs font-semibold text-slate-500 dark:text-slate-400"
              >
                Email, Discord username, or @username
              </label>
              <FriendCombobox
                inputId="invite-member-combobox"
                friends={suggestionUsers}
                recentInvitees={recentInvitees}
                inputValue={email}
                clearOnSelect={false}
                onInputChange={(value) => {
                  setEmail(value);
                  const nextNormalized = normalizeEmail(value);
                  if (!nextNormalized || friendSet.has(nextNormalized)) {
                    setSendFriendInvite(false);
                  }
                  setError(null);
                }}
                onSelect={(suggestedEmail) => {
                  setEmail(suggestedEmail);
                  setSendFriendInvite(false);
                  setError(null);
                }}
                placeholder="friend@example.com, discord_username, or @username"
                label="From your friends"
              />
            </div>

            {error && (
              <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
            )}

            {showFriendInviteToggle && (
              <label className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={sendFriendInvite}
                  onChange={(e) => setSendFriendInvite(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-primary focus:ring-brand-primary dark:border-slate-600"
                />
                <span>Also send a friend request.</span>
              </label>
            )}
          </div>

          <SimpleModalFooter className="mt-6">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !email.trim()}
              className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
            >
              {saving ? "Sending..." : "Send invitation"}
            </button>
          </SimpleModalFooter>
        </form>
      </div>
    </SimpleModal>
  );
}
